import React, { useState, useEffect } from 'react';
import { 
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart,
  LineChart,
  Activity,
  Brain,
  Target,
  AlertTriangle,
  CheckCircle,
  Clock,
  DollarSign,
  Users,
  Building,
  Calendar,
  FileText,
  Download,
  RefreshCw,
  Settings,
  Filter,
  Search,
  Eye,
  Edit,
  Trash2,
  Plus,
  Star,
  Bookmark,
  Share,
  MoreHorizontal,
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeft,
  Home,
  Wrench,
  FileBarChart,
  Cog,
  Shield,
  Lock,
  Link,
  Zap,
  Globe,
  Mic,
  Languages,
  Users2,
  Calculator,
  Receipt,
  CreditCard,
  MapPin,
  Phone,
  Mail,
  Smartphone,
  Monitor,
  Tablet,
  Key,
  Fingerprint,
  Lightbulb,
  Cpu,
  Database,
  Network,
  Layers,
  Upload,
  FileUp,
  FileDown,
  CheckSquare,
  Square,
  XCircle,
  Info,
  ExternalLink,
  Archive,
  Send,
  AlertCircle,
  Flag,
  Heart,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  User,
  Save
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ActionMenu from '../common/ActionMenu';
import ConfirmationModal from '../common/ConfirmationModal';
import AIService from '../../services/aiService';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const PredictiveAnalytics = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedMetric, setSelectedMetric] = useState('all');

  // Mock data for predictive analytics
  const [analyticsData, setAnalyticsData] = useState([
    {
      id: 'ANALYTICS-001',
      name: 'Contract Renewal Risk Analysis',
      type: 'Risk Assessment',
      category: 'Contract Management',
      status: 'Active',
      priority: 'High',
      accuracy: 94,
      lastRun: '2024-02-16T10:30:00Z',
      nextRun: '2024-02-17T10:30:00Z',
      dataPoints: 1250,
      predictions: 45,
      confidence: 92,
      trend: 'up',
      value: '$2.3M',
      description: 'Predicts contract renewal risks based on historical data and current performance metrics',
      insights: [
        '15 contracts at high risk of non-renewal',
        '3 contracts require immediate attention',
        'Average renewal probability: 78%'
      ],
      recommendations: [
        'Review performance metrics for high-risk contracts',
        'Schedule renewal discussions 90 days early',
        'Implement performance improvement plans'
      ],
      isActive: true,
      tags: ['contracts', 'renewal', 'risk', 'prediction'],
      createdBy: 'AI Analytics Engine',
      department: 'Legal',
      modelType: 'Machine Learning',
      algorithm: 'Random Forest',
      trainingData: '2 years',
      isBookmarked: true,
      isStarred: false,
      viewCount: 67,
      downloadCount: 12
    },
    {
      id: 'ANALYTICS-002',
      name: 'Asset Depreciation Forecast',
      type: 'Financial Forecast',
      category: 'Asset Management',
      status: 'Active',
      priority: 'Medium',
      accuracy: 89,
      lastRun: '2024-02-15T14:20:00Z',
      nextRun: '2024-02-16T14:20:00Z',
      dataPoints: 890,
      predictions: 120,
      confidence: 87,
      trend: 'down',
      value: '$1.8M',
      description: 'Forecasts asset depreciation and replacement costs using historical usage patterns',
      insights: [
        '23 assets require replacement in Q2',
        'Total replacement cost: $1.2M',
        'Optimal replacement timeline identified'
      ],
      recommendations: [
        'Budget for Q2 asset replacements',
        'Consider leasing options for high-value assets',
        'Implement preventive maintenance schedule'
      ],
      isActive: true,
      tags: ['assets', 'depreciation', 'forecast', 'replacement'],
      createdBy: 'AI Analytics Engine',
      department: 'Finance',
      modelType: 'Time Series',
      algorithm: 'ARIMA',
      trainingData: '3 years',
      isBookmarked: false,
      isStarred: true,
      viewCount: 45,
      downloadCount: 8
    },
    {
      id: 'ANALYTICS-003',
      name: 'Compliance Risk Prediction',
      type: 'Risk Assessment',
      category: 'Compliance',
      status: 'Active',
      priority: 'High',
      accuracy: 91,
      lastRun: '2024-02-14T09:15:00Z',
      nextRun: '2024-02-15T09:15:00Z',
      dataPoints: 2100,
      predictions: 78,
      confidence: 88,
      trend: 'stable',
      value: '$850K',
      description: 'Predicts compliance risks and potential violations using regulatory data and audit history',
      insights: [
        '5 areas at risk of compliance violations',
        '2 critical issues require immediate action',
        'Overall compliance score: 87%'
      ],
      recommendations: [
        'Implement additional controls for high-risk areas',
        'Schedule compliance training for affected teams',
        'Review and update compliance policies'
      ],
      isActive: true,
      tags: ['compliance', 'risk', 'regulatory', 'audit'],
      createdBy: 'AI Analytics Engine',
      department: 'Legal',
      modelType: 'Classification',
      algorithm: 'Logistic Regression',
      trainingData: '4 years',
      isBookmarked: true,
      isStarred: false,
      viewCount: 89,
      downloadCount: 15
    }
  ]);

  // Mock forecast data
  const [forecastData, setForecastData] = useState([
    { month: 'Jan 2024', actual: 120000, predicted: 118000, confidence: 92 },
    { month: 'Feb 2024', actual: 135000, predicted: 132000, confidence: 89 },
    { month: 'Mar 2024', actual: 142000, predicted: 145000, confidence: 87 },
    { month: 'Apr 2024', actual: null, predicted: 158000, confidence: 85 },
    { month: 'May 2024', actual: null, predicted: 165000, confidence: 83 },
    { month: 'Jun 2024', actual: null, predicted: 172000, confidence: 81 }
  ]);

  // Calculate statistics
  const stats = {
    totalModels: analyticsData.length,
    activeModels: analyticsData.filter(m => m.status === 'Active').length,
    averageAccuracy: analyticsData.reduce((sum, m) => sum + m.accuracy, 0) / analyticsData.length,
    totalPredictions: analyticsData.reduce((sum, m) => sum + m.predictions, 0),
    highRiskItems: analyticsData.filter(m => m.priority === 'High').length,
    totalValue: analyticsData.reduce((sum, m) => sum + parseFloat(m.value.replace(/[$,]/g, '')) / 1000, 0)
  };

  // Filtered data
  const filteredAnalytics = analyticsData.filter(item => {
    const matchesMetric = selectedMetric === 'all' || item.category === selectedMetric;
    return matchesMetric;
  });

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'forecasts', label: 'Forecasts' },
    { id: 'models', label: 'AI Models' },
    { id: 'insights', label: 'Insights' },
    { id: 'reports', label: 'Reports' }
  ];

  // Define columns for DataTable
  const columns = [
    {
      key: 'name',
      label: 'Analytics Model',
      width: '300px',
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}>{value}</div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {row.type} • {row.modelType} • {row.algorithm}
          </div>
        </div>
      )
    },
    {
      key: 'accuracy',
      label: 'Accuracy',
      width: '120px',
      render: (accuracy) => {
        const getAccuracyColor = (accuracy) => {
          if (accuracy >= 90) return '#10b981';
          if (accuracy >= 80) return '#f59e0b';
          return '#ef4444';
        };
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '60px',
              height: '6px',
              backgroundColor: '#e5e7eb',
              borderRadius: '3px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${accuracy}%`,
                height: '100%',
                backgroundColor: getAccuracyColor(accuracy),
                transition: 'width 0.3s ease'
              }} />
            </div>
            <span style={{ fontSize: '12px', fontWeight: '500', color: getAccuracyColor(accuracy) }}>
              {accuracy}%
            </span>
          </div>
        );
      }
    },
    {
      key: 'predictions',
      label: 'Predictions',
      width: '100px',
      render: (count) => (
        <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
          {count}
        </div>
      )
    },
    {
      key: 'value',
      label: 'Impact Value',
      width: '120px',
      render: (value) => (
        <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
          {value}
        </div>
      )
    },
    {
      key: 'lastRun',
      label: 'Last Run',
      width: '120px',
      render: (date) => new Date(date).toLocaleDateString()
    }
  ];

  // Handler functions
  const handleView = (model) => {
    //console.log('Viewing analytics model:', model.name);
  };

  const handleRun = async (model) => {
    setIsGenerating(true);
    try {
      // Call AI service to run the predictive model
      const response = await AIService.runModel(model.id, {
        timeRange: timeRange,
        metrics: selectedMetric
      });
      
      if (response.success) {
        //console.log('Model execution results:', response.modelResult);
        // Update model data with new results
        setAnalyticsData(prev => prev.map(item => 
          item.id === model.id 
            ? { 
                ...item, 
                lastRun: new Date().toISOString(),
                accuracy: response.modelResult.performance.accuracy,
                predictions: response.modelResult.results.length
              }
            : item
        ));
      }
    } catch (error) {
      console.error('Error running model:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDelete = (model) => {
    setSelectedItem(model);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      setAnalyticsData(prev => prev.filter(item => item.id !== selectedItem.id));
      setShowDeleteModal(false);
      setSelectedItem(null);
    } catch (error) {
      console.error('Error deleting model:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleGenerateInsights = async () => {
    setIsGenerating(true);
    try {
      // Call AI service for insights generation
      const response = await AIService.generateInsights(
        selectedMetric,
        timeRange,
        { category: selectedMetric, timeRange: timeRange }
      );
      
      if (response.success) {
        //console.log('Generated insights:', response.analytics);
        // Update analytics data with new insights
        // In a real implementation, you would update the insights state
      }
    } catch (error) {
      console.error('Error generating insights:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="page-container">
      <PageBreadcrumb />
      <PageHeader 
        title="Predictive Analytics"
        description="AI-powered forecasting, risk assessment, and predictive modeling for contracts and assets"
      />

      {/* Tabbed Navigation */}
      <div className="page-tabs">
        <div className="page-tabs-list">
          {tabs.map(tab => (
            <div key={tab.id} className="page-tab-item">
              <button
                className={`page-tab-link ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.id === 'overview' && <BarChart3 size={18} className="page-tab-icon" />}
                {tab.id === 'forecasts' && <TrendingUp size={18} className="page-tab-icon" />}
                {tab.id === 'models' && <Brain size={18} className="page-tab-icon" />}
                {tab.id === 'insights' && <Lightbulb size={18} className="page-tab-icon" />}
                {tab.id === 'reports' && <FileText size={18} className="page-tab-icon" />}
                {tab.label}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="page-stats-grid">
        <div className="page-kpi-card color-1">
          <div className="page-kpi-body">
            <div className="page-kpi-content">
              <div className="page-kpi-text">
                <div className="page-kpi-value">{stats.activeModels}</div>
                <div className="page-kpi-label">Active Models</div>
              </div>
              <div className="page-kpi-icon-container">
                <Brain size={20} className="page-kpi-icon" />
              </div>
            </div>
          </div>
        </div>

        <div className="page-kpi-card color-2">
          <div className="page-kpi-body">
            <div className="page-kpi-content">
              <div className="page-kpi-text">
                <div className="page-kpi-value">{Math.round(stats.averageAccuracy)}%</div>
                <div className="page-kpi-label">Avg. Accuracy</div>
              </div>
              <div className="page-kpi-icon-container">
                <Target size={20} className="page-kpi-icon" />
              </div>
            </div>
          </div>
        </div>

        <div className="page-kpi-card color-3">
          <div className="page-kpi-body">
            <div className="page-kpi-content">
              <div className="page-kpi-text">
                <div className="page-kpi-value">{stats.totalPredictions}</div>
                <div className="page-kpi-label">Total Predictions</div>
              </div>
              <div className="page-kpi-icon-container">
                <TrendingUp size={20} className="page-kpi-icon" />
              </div>
            </div>
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #e5e7eb'
        }}>
          <div>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0' }}>Impact Value</p>
            <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 }}>
              ${stats.totalValue.toFixed(1)}M
            </h2>
          </div>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: '#8b5cf620',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#8b5cf6'
          }}>
            <DollarSign size={20} />
          </div>
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div>
          {/* Key Metrics */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px',
            marginBottom: '32px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                margin: '0 0 16px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <AlertTriangle size={20} style={{ color: '#f59e0b' }} />
                High Risk Items
              </h3>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#f59e0b', marginBottom: '8px' }}>
                {stats.highRiskItems}
              </div>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                Items requiring immediate attention
              </p>
            </div>

            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                margin: '0 0 16px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <CheckCircle size={20} style={{ color: '#10b981' }} />
                Model Performance
              </h3>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#10b981', marginBottom: '8px' }}>
                {Math.round(stats.averageAccuracy)}%
              </div>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                Average prediction accuracy
              </p>
            </div>

            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                margin: '0 0 16px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Activity size={20} style={{ color: 'var(--primary-color)' }} />
                Data Points
              </h3>
              <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--primary-color)', marginBottom: '8px' }}>
                {analyticsData.reduce((sum, m) => sum + m.dataPoints, 0).toLocaleString()}
              </div>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                Total data points analyzed
              </p>
            </div>
          </div>

          {/* Recent Analytics */}
          <div className="page-table-container">
            <DataTable
              data={filteredAnalytics}
              columns={columns}
              title="Predictive Analytics Models"
              searchable={true}
              sortable={true}
              exportable={true}
              pagination={true}
              pageSize={10}
              showActions={true}
              showCheckboxes={false}
              onView={handleView}
              onEdit={handleRun}
              onDelete={handleDelete}
              searchPlaceholder="Search analytics models..."
              emptyMessage="No analytics models found."
              initialHiddenColumns={['id']}
            />
          </div>
        </div>
      )}

      {/* Forecasts Tab */}
      {activeTab === 'forecasts' && (
        <div>
          {/* Forecast Controls */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                margin: 0
              }}>
                Forecast Controls
              </h3>
              <div style={{ display: 'flex', gap: '12px' }}>
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                >
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="90d">Last 90 days</option>
                  <option value="1y">Last year</option>
                </select>
                <button
                  onClick={handleGenerateInsights}
                  disabled={isGenerating}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    backgroundColor: isGenerating ? '#9ca3af' : 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: isGenerating ? 'not-allowed' : 'pointer',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                >
                  {isGenerating ? (
                    <>
                      <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Brain size={16} />
                      Generate Forecast
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Forecast Chart Placeholder */}
            <div style={{
              height: '300px',
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              color: '#6b7280'
            }}>
              Interactive Forecast Chart (Chart.js/Recharts integration)
            </div>
          </div>

          {/* Forecast Data Table */}
          <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
            <DataTable
              data={forecastData}
              columns={[
                {
                  key: 'month',
                  label: 'Month',
                  width: '120px'
                },
                {
                  key: 'actual',
                  label: 'Actual',
                  width: '120px',
                  render: (value) => value ? `$${value.toLocaleString()}` : '-'
                },
                {
                  key: 'predicted',
                  label: 'Predicted',
                  width: '120px',
                  render: (value) => `$${value.toLocaleString()}`
                },
                {
                  key: 'confidence',
                  label: 'Confidence',
                  width: '120px',
                  render: (confidence) => `${confidence}%`
                }
              ]}
              title={`Forecast Data (${forecastData.length} periods)`}
              searchable={false}
              sortable={true}
              exportable={true}
              pagination={true}
              pageSize={10}
              hiddenColumns={['none']}
              onHiddenColumnsChange={() => {}}
            />
          </div>
        </div>
      )}

      {/* AI Models Tab */}
      {activeTab === 'models' && (
        <div>
          {/* Model Management */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                margin: 0
              }}>
                AI Model Management
              </h3>
              <div style={{ display: 'flex', gap: '12px' }}>
                <select
                  value={selectedMetric}
                  onChange={(e) => setSelectedMetric(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                >
                  <option value="all">All Categories</option>
                  <option value="Contract Management">Contract Management</option>
                  <option value="Asset Management">Asset Management</option>
                  <option value="Compliance">Compliance</option>
                </select>
                <button
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    backgroundColor: 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                >
                  <Plus size={16} />
                  New Model
                </button>
              </div>
            </div>

            {/* Model Performance Metrics */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              marginBottom: '20px'
            }}>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
                  {analyticsData.filter(m => m.modelType === 'Machine Learning').length}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>ML Models</div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
                  {analyticsData.filter(m => m.modelType === 'Time Series').length}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Time Series</div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
                  {analyticsData.filter(m => m.modelType === 'Classification').length}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Classification</div>
              </div>
            </div>
          </div>

          {/* Models DataTable */}
          <div className="page-table-container">
            <DataTable
              data={filteredAnalytics}
              columns={columns}
              title="AI Models"
              searchable={true}
              sortable={true}
              exportable={true}
              pagination={true}
              pageSize={10}
              showActions={true}
              showCheckboxes={false}
              onView={handleView}
              onEdit={handleRun}
              onDelete={handleDelete}
              searchPlaceholder="Search AI models..."
              emptyMessage="No AI models found."
              initialHiddenColumns={['id']}
            />
          </div>
        </div>
      )}

      {/* Insights Tab */}
      {activeTab === 'insights' && (
        <div>
          {/* AI Insights Generation */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 20px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Lightbulb size={20} style={{ color: 'var(--primary-color)' }} />
              AI-Generated Insights
            </h3>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '20px'
            }}>
              {analyticsData.map((model) => (
                <div key={model.id} style={{
                  padding: '20px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1f2937',
                    margin: '0 0 12px 0'
                  }}>
                    {model.name}
                  </h4>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151',
                      margin: '0 0 8px 0'
                    }}>
                      Key Insights:
                    </h5>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      {model.insights.map((insight, index) => (
                        <li key={index} style={{
                          fontSize: '14px',
                          color: '#6b7280',
                          marginBottom: '4px'
                        }}>
                          {insight}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151',
                      margin: '0 0 8px 0'
                    }}>
                      Recommendations:
                    </h5>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      {model.recommendations.map((rec, index) => (
                        <li key={index} style={{
                          fontSize: '14px',
                          color: '#6b7280',
                          marginBottom: '4px'
                        }}>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          minHeight: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          color: '#6b7280'
        }}>
          Predictive Analytics Reports Dashboard Coming Soon!
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Analytics Model"
        message={`Are you sure you want to delete the analytics model "${selectedItem?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />

      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PredictiveAnalytics;
